import * as THREE from 'three';

// ─────────────────────────────────────────────────────────────
// Domain string-literal unions — define `const + typeof` pairs
// so call sites use HAND.left, ZONE.head, etc. (no enums).
// ─────────────────────────────────────────────────────────────

/** XR handedness values (matches the WebXR `XRHandedness` spec). */
export const HAND = {
  left: 'left',
  right: 'right',
  none: 'none',
} as const;
export type Hand = (typeof HAND)[keyof typeof HAND];

/** Subset of HAND used as the user's dominant (sword) hand — excludes 'none'. */
export const DOMINANT_HAND = {
  left: HAND.left,
  right: HAND.right,
} as const;
export type DominantHand = (typeof DOMINANT_HAND)[keyof typeof DOMINANT_HAND];

/** Rotation mode for the right-thumbstick yaw. */
export const TURN_MODE = {
  snap15: 'snap15',
  snap30: 'snap30',
  snap45: 'snap45',
  continuous: 'continuous',
} as const;
export type TurnMode = (typeof TURN_MODE)[keyof typeof TURN_MODE];

/** Latched direction for snap-turn rising-edge detection. */
export const TURN_SNAP = {
  neutral: 'neutral',
  right: 'right',
  left: 'left',
} as const;
export type TurnSnap = (typeof TURN_SNAP)[keyof typeof TURN_SNAP];

/** Whether the session is bounded by a timer, runs freely, or ends after N correct reps. */
export const DURATION_MODE = {
  timed: 'timed',
  free: 'free',
  reps: 'reps', // prescriptive drill: ends when the rep count is reached
} as const;
export type DurationMode = (typeof DURATION_MODE)[keyof typeof DURATION_MODE];

/**
 * Which drill the user is running: pell (cuts on a post), targets (mid-air thrust drill),
 * guard (free static-guard recognition readout — issue #16, observational cycle), or
 * arcade (the "Sfida" 60s directional-cut score attack — issue #34).
 */
export const DRILL_TYPE = {
  pell: 'pell',
  guard: 'guard',
  arcade: 'arcade',
} as const;
export type DrillType = (typeof DRILL_TYPE)[keyof typeof DRILL_TYPE];

/**
 * Which gesture an arcade run is resolved by: `cut` (directional slice, the original
 * "Sfida") or `thrust` (mid-air punta, absorbed from the former targets drill — #44).
 * Threaded through the wizard, runtimes, scoring, stats, settings, and leaderboard so a
 * cut run and a thrust run never share a board or a personal best.
 */
export const TECHNIQUE = {
  cut: 'cut',
  thrust: 'thrust',
} as const;
export type Technique = (typeof TECHNIQUE)[keyof typeof TECHNIQUE];

/**
 * Difficulty selected before an arcade run (#43). The four presets are read-only bundles over
 * the existing tunables (cadence, acceptance, min speed, disc size); `custom` is the escape
 * hatch that reads the manual settings sliders instead and never records a score (no best, no
 * leaderboard, no history). Stored only on the per-run config — never a persisted setting.
 * Internal keys stay plain (`easy…xhard`); the d'arte display labels live in i18n.
 */
export const DIFFICULTY = {
  easy: 'easy',
  medium: 'medium',
  hard: 'hard',
  xhard: 'xhard',
  custom: 'custom',
} as const;
export type Difficulty = (typeof DIFFICULTY)[keyof typeof DIFFICULTY];

/** The four preset levels only — `custom` is excluded (it has no bundle and never scores). */
export type DifficultyPreset = Exclude<Difficulty, 'custom'>;

/** Selector order (custom last). */
export const DIFFICULTY_ORDER = [
  DIFFICULTY.easy,
  DIFFICULTY.medium,
  DIFFICULTY.hard,
  DIFFICULTY.xhard,
  DIFFICULTY.custom,
] as const;

/** Preselected difficulty in the wizard. */
export const DIFFICULTY_DEFAULT: Difficulty = DIFFICULTY.medium;

/** Top-level app state machine. */
export const APP_STATE = {
  wizard: 'wizard',
  running: 'running',
  paused: 'paused',
  summary: 'summary',
} as const;
export type AppState = (typeof APP_STATE)[keyof typeof APP_STATE];

/** Internal state of a SessionTimer. */
export const TIMER_STATE = {
  idle: 'idle',
  running: 'running',
  paused: 'paused',
  ended: 'ended',
} as const;
export type TimerState = (typeof TIMER_STATE)[keyof typeof TIMER_STATE];

/** Vertical hit zones on the pell (top → bottom). */
export const ZONE = {
  head: 'head',
  shoulder: 'shoulder',
  chest: 'chest',
  flank: 'flank',
  leg: 'leg',
} as const;
export type Zone = (typeof ZONE)[keyof typeof ZONE];

/** Quality classification for a swing. `thrust` is a separate kind (colpo di punta). */
export const EDGE_QUALITY = {
  good: 'good',
  ok: 'ok',
  flat: 'flat',
  thrust: 'thrust',
} as const;
export type EdgeQuality = (typeof EDGE_QUALITY)[keyof typeof EDGE_QUALITY];

// ─────────────────────────────────────────────────────────────
// Stroke classification (colpo bolognese) — d'arte names are the canonical
// literals; they don't translate (Q15). Three orthogonal axes per cut.
// ─────────────────────────────────────────────────────────────

/** Side the cut comes from, in the swordsman's body frame (flips with dominant hand). */
export const LATO = {
  mandritto: 'mandritto',
  riverso: 'riverso',
} as const;
export type Lato = (typeof LATO)[keyof typeof LATO];

/** Inclination of the cutting arc in the vertical plane (descending → horizontal → ascending). */
export const INCLINAZIONE = {
  fendente: 'fendente', // near-vertical descending
  sgualembro: 'sgualembro', // diagonal descending
  tondo: 'tondo', // horizontal (mezzo)
  ridoppio: 'ridoppio', // diagonal ascending
  montante: 'montante', // near-vertical ascending
} as const;
export type Inclinazione = (typeof INCLINAZIONE)[keyof typeof INCLINAZIONE];

/** Which edge leads the cut at impact. */
export const FILO = {
  dritto: 'dritto', // true edge (filo dritto, +Y local)
  falso: 'falso', // back edge (filo falso)
} as const;
export type Filo = (typeof FILO)[keyof typeof FILO];

// ─────────────────────────────────────────────────────────────
// Angle conversion
// ─────────────────────────────────────────────────────────────

export const DEG_TO_RAD = Math.PI / 180;
export const RAD_TO_DEG = 180 / Math.PI;

// ─────────────────────────────────────────────────────────────
// localStorage keys (single source — keep names ↔ values in sync)
// ─────────────────────────────────────────────────────────────

export const STORAGE_KEYS = {
  userHeightCm: 'userHeightCm',
  dominantHand: 'dominantHand',
  turnMode: 'turnMode',
  swordPitchDeg: 'swordPitchDeg',
  swordYawDeg: 'swordYawDeg',
  swordRollDeg: 'swordRollDeg',
  sessionPrefix: 'session_',
  sessionIndex: 'session_index',
  targetsSizeFactor: 'targetsSizeFactor',
  targetsMinSpeed: 'targetsMinSpeed',
  targetsDistance: 'targetsDistance',
  targetsTtl: 'targetsTtl',
  targetsSpawnDelay: 'targetsSpawnDelay',
  pellZoneBands: 'pellZoneBands',
  /** Arcade cut ("Sfida") local personal best — kept out of UserSettings so a reset never clears it. */
  arcadeBest: 'arcadeBest',
  /** Arcade thrust (punte) local personal best — separate board, never mixed with the cut best (#44). */
  arcadeBestThrust: 'arcadeBestThrust',
} as const;

// ─────────────────────────────────────────────────────────────
// Sword geometry (m)
// ─────────────────────────────────────────────────────────────

export const SWORD_BLADE_LENGTH_M = 0.95;
export const SWORD_GRIP_LENGTH_M = 0.15;
export const SWORD_QUILLON_WIDTH_M = 0.2;

/** Default sword orientation in the controller frame (user-tunable in settings). */
export const SWORD_PITCH_DEFAULT_DEG = 25;
export const SWORD_ROLL_DEFAULT_DEG = -90;

/** Sword-local axes (controller frame). Read-only — clone before transforming. */
export const SWORD_EDGE_AXIS_LOCAL = new THREE.Vector3(1, 0, 0);
export const SWORD_BLADE_AXIS_LOCAL = new THREE.Vector3(0, 0, -1);

// ─────────────────────────────────────────────────────────────
// Pell auto-placement (m)
// ─────────────────────────────────────────────────────────────

/** Distance in front of the user where the pell auto-spawns at session start. */
export const PELL_AUTO_DISTANCE_M = 1.5;

// ─────────────────────────────────────────────────────────────
// Pell zones (vertical bands as fraction of user height)
// ─────────────────────────────────────────────────────────────

/** Lower bound (fraction of user height) per zone. Upper bound = next zone's lower. */
/** Default zone height bands (fraction of body height). User-tunable via settings (see `zoneBands`). */
export const PELL_ZONE_BOUNDS: Record<Zone, readonly [number, number]> = {
  leg: [0.0, 0.5],
  flank: [0.5, 0.6],
  chest: [0.6, 0.82],
  shoulder: [0.82, 0.92],
  head: [0.92, 1.0],
};

/** Step (fraction of height) when tuning an internal zone boundary. */
export const PELL_ZONE_BAND_STEP = 0.02;
/** Minimum gap (fraction of height) kept between adjacent zone boundaries. */
export const PELL_ZONE_BAND_MIN_GAP = 0.02;

export const PELL_ZONE_COLORS: Record<Zone, number> = {
  leg: 0x6a6a6a,
  flank: 0xc9b341,
  chest: 0xd97a2a,
  shoulder: 0xd44a3a,
  head: 0xb03020,
};

/** Display order, top → bottom. */
export const PELL_ZONE_ORDER: readonly Zone[] = [
  ZONE.head,
  ZONE.shoulder,
  ZONE.chest,
  ZONE.flank,
  ZONE.leg,
];

// ─────────────────────────────────────────────────────────────
// Edge alignment thresholds (deg between blade edge and cut plane)
// ─────────────────────────────────────────────────────────────

export const EDGE_ALIGN_GOOD_DEG = 15;
export const EDGE_ALIGN_OK_DEG = 30;

// ─────────────────────────────────────────────────────────────
// Hit-feedback decal (m + colors)
// ─────────────────────────────────────────────────────────────

export const DECAL_SURFACE_OFFSET_M = 0.003;
export const DECAL_NAME = '__hitDecal';

// Cut mark: a white slash (the movement) plus a smaller quality-coloured leaf at the
// impact point showing the real filo. Both are pointed leaf/vesica shapes; widths below
// are the *full* thickness at the belly.

/** White slash thickness (full, at the belly). */
export const DECAL_CUT_LEAF_WIDTH_M = 0.05;
/** White slash length = clamp(swingArcM * DECAL_CUT_ARC_TO_LEN, MIN, MAX). */
export const DECAL_CUT_LEN_MIN_M = 0.24;
export const DECAL_CUT_LEN_MAX_M = 0.72;
export const DECAL_CUT_ARC_TO_LEN = 0.64;
/** Slash length used when the swing-arc length is unknown (e.g. arcade cut). */
export const DECAL_CUT_LEN_DEFAULT_M = 0.44;
export const DECAL_CUT_COLOR = 0xffffff;

/** Filo leaf at the impact point — kept shorter & thinner than the white slash so a
 *  clean (aligned) cut nests it fully inside; a piattonata shows it crossing at an angle. */
export const DECAL_FILO_LEAF_LEN_M = 0.2;
export const DECAL_FILO_LEAF_WIDTH_M = 0.03;

/** Curve tessellation for each leaf ShapeGeometry. */
export const DECAL_LEAF_CURVE_SEGMENTS = 24;

export const DECAL_QUALITY_COLORS: Record<EdgeQuality, number> = {
  good: 0x4ade80,
  ok: 0xfacc15,
  flat: 0xef4444,
  thrust: 0x3b82f6,
};

/** Thrust marker geometry: a blue ring with a small white dot at the center. */
export const DECAL_THRUST_RING_OUTER_M = 0.05;
export const DECAL_THRUST_RING_INNER_M = 0.038;
export const DECAL_THRUST_DOT_RADIUS_M = 0.012;
export const DECAL_THRUST_DOT_COLOR = 0xffffff;

/**
 * Thrust detection: minimum |cos(angle)| between tip velocity and blade axis.
 * 0.85 ≈ within ~32° of pure thrust.
 */
export const THRUST_ALIGN_MIN_COS = 0.85;

// ─────────────────────────────────────────────────────────────
// XR locomotion (thumbstick translation + yaw)
// ─────────────────────────────────────────────────────────────

export const LOCO_MOVE_SPEED_MS = 2.0;
export const LOCO_DEAD_ZONE = 0.15;
export const LOCO_CONT_TURN_SPEED_DEG = 90;
export const LOCO_SNAP_TRIGGER = 0.7;
export const LOCO_SNAP_RELEASE = 0.3;

// ─────────────────────────────────────────────────────────────
// XR gamepad button bindings (left controller)
// ─────────────────────────────────────────────────────────────

/** Index in `gamepad.buttons` for the settings toggle (Y face button on Quest 3). */
export const XR_BUTTON_SETTINGS_INDEX = 5;

// ─────────────────────────────────────────────────────────────
// Settings panel slider ranges
// ─────────────────────────────────────────────────────────────

export const SETTINGS_HEIGHT_MIN_CM = 120;
export const SETTINGS_HEIGHT_MAX_CM = 220;
export const SETTINGS_HEIGHT_STEP_CM = 5;
export const SETTINGS_ANGLE_MIN_DEG = -90;
export const SETTINGS_ANGLE_MAX_DEG = 90;
export const SETTINGS_ANGLE_STEP_DEG = 5;

// ─────────────────────────────────────────────────────────────
// VR panel placement (m) — unified across all panels
// ─────────────────────────────────────────────────────────────

/** Distance from user to every UI panel. Kept smaller than PELL_AUTO_DISTANCE_M so the pell stays behind the panel. */
export const PANEL_DIST_M = 1.2;
/** Panel center Y as a fraction of the user's configured height (0.92 ≈ eye level). */
export const PANEL_CENTER_Y_FACTOR = 0.92;
/** Floor used when userHeightCm is missing/invalid (guards camY=0 at session start). */
export const PANEL_MIN_CENTER_Y_M = 1.1;

export const PAUSE_PANEL_WIDTH_M = 0.7;
export const PAUSE_PANEL_HEIGHT_M = 0.4;

export const SETTINGS_PANEL_WIDTH_M = 1.38;
export const SETTINGS_PANEL_HEIGHT_M = 1.2;

export const SUMMARY_PANEL_WIDTH_M = 1.15;
export const SUMMARY_PANEL_HEIGHT_M = 0.82;

export const WIZARD_PANEL_WIDTH_M = 1.2;
export const WIZARD_PANEL_HEIGHT_M = 0.78;

// ─────────────────────────────────────────────────────────────
// Session
// ─────────────────────────────────────────────────────────────

export const SESSION_DURATION_OPTIONS_MS = [
  30_000, // 30 s — quick test mode
  5 * 60_000,
  10 * 60_000,
  15 * 60_000,
] as const;

// ─────────────────────────────────────────────────────────────
// UI palette — pagina di trattato a stampa (Marozzo/Manciolino style)
// ─────────────────────────────────────────────────────────────

// Text
export const UI_INK = '#241408'; // inchiostro su pergamena
export const UI_INK_MUTED = '#6b4a28'; // labels secondarie su pergamena
export const UI_IVORY = '#f0e6d2'; // testo su ceralacca (bottoni primary)

// Stati
export const UI_SEAL_RED = '#b94a2a'; // ceralacca → primary button + accenti
export const UI_VERDIGRIS = '#5e8a4a'; // verde "buono"
export const UI_BLOOD = '#a52a1a'; // rosso scuro "errore/avviso"

export const UI_GOOD = UI_VERDIGRIS;
export const UI_BAD = UI_BLOOD;

/** Three.js hex equivalents (THREE.Color / setHex). */
export const UI_BUTTON_BG_HEX = 0xb94a2a;
export const UI_GOOD_HEX = 0x5e8a4a;
export const UI_BAD_HEX = 0xa52a1a;
export const UI_INK_HEX = 0x241408;

// Typography
export const UI_FONT_DISPLAY = "'Cinzel', 'Trajan Pro', Georgia, serif";
export const UI_FONT_NUMERIC = "'JetBrains Mono', 'Cascadia Code', monospace";
/** Treatise-page body font (issues #28–#30). */
export const UI_FONT_BODY_TREATISE = "'EB Garamond', Georgia, serif";

// Frame geometry (canvas px @ PANEL_PIXELS_PER_METER = 1024)
export const PANEL_PIXELS_PER_METER = 1024;
export const UI_DIVIDER_HEIGHT_PX = 22;

// Texture URLs (CC0 — parchment from ambientCG)
export const UI_TEXTURE_PARCHMENT_URL = '/textures/ui_parchment.jpg';

// ─────────────────────────────────────────────────────────────
// Treatise-page panel style (issue #28) — printed Opera Nova page
// ─────────────────────────────────────────────────────────────

// Public-domain assets: segno from Marozzo's Opera Nova 1568 (Wiktenauer scan,
// levels-cleaned), hedera = Aldine leaf fleuron (Wikimedia Commons, PD).
export const UI_TEXTURE_SEGNO_URL = '/textures/ui_segno.png';
export const UI_TEXTURE_HEDERA_URL = '/textures/ui_hedera.svg';
/** Quill feather for the menu pointer — CC BY-SA 4.0, Venicescapes (see CREDITS.md). */
export const UI_TEXTURE_QUILL_URL = '/textures/ui_quill.png';

// Page geometry (canvas px @ PANEL_PIXELS_PER_METER)
export const UI_PAGE_MARGIN_PX = 26; // paper margin outside the rule frame
export const UI_PAGE_RULE_GAP_PX = 7; // gap between the double rules
export const UI_PAGE_EDGE_PX = 14; // darkened deckle band at the paper edge
export const UI_PAGE_HEDERA_PX = 44; // corner fleuron size
export const UI_PAGE_CONTENT_PAD_PX = 20; // padding between rule frame and content
export const UI_PAGE_FILIGRANA_ALPHA = 0.14; // segno watermark (multiply blend)
/** Stronger segno on the colpo-picker wizard step (it diagrams the cut names shown). */
export const DRILL_TARGET_FILIGRANA_ALPHA = 0.24;

// Ink on paper
export const UI_PAPER_LIGHT = '#f0e2b6'; // page centre
export const UI_PAPER_DARK = '#d9c188'; // page edges / aged tone

/** Key written into THREE.Object3D.userData to attach a pick handler. */
export const INTERACTIVE_USERDATA_KEY = 'onPick';

// ─────────────────────────────────────────────────────────────
// Mesh names (THREE.Object3D.name) — used as identifiers in scene lookups
// ─────────────────────────────────────────────────────────────

export const MESH_NAME_SWORD = 'sword';
export const MESH_NAME_PELL = 'pell';
export const MESH_NAME_QUILL = 'quill';

// ─────────────────────────────────────────────────────────────
// Scene setup (background, lights, floor, camera)
// ─────────────────────────────────────────────────────────────

/** Selectable training environments. The choice persists in `UserSettings` (synced). */
export const ENVIRONMENT_ID = {
  forest: 'forest',
  salaDArmi: 'salaDArmi',
  /** Passthrough ("Realtà"): the real surroundings instead of a virtual hall (immersive-ar). */
  passthrough: 'passthrough',
} as const;
export type EnvironmentId = (typeof ENVIRONMENT_ID)[keyof typeof ENVIRONMENT_ID];

/** Fields shared by every environment kind: the HDRI (lighting) and its IBL intensity. */
type EnvironmentBase = {
  id: EnvironmentId;
  /**
   * Equirectangular HDRI. Provides image-based lighting (`scene.environment`) for every
   * environment; for virtual ones it is also rendered as the visible background, for
   * passthrough it lights the blade/targets only (the camera feed is the background).
   */
  hdriUrl: string;
  /** Multiplier for scene.environment IBL intensity (1.0 = HDRI raw, lower = less reflective). */
  envIntensity: number;
};

/** A fully virtual environment: opaque HDRI background + textured floor (immersive-vr). */
export type VirtualEnvironmentDef = EnvironmentBase & {
  /** Discriminant: absent/false here, `true` on the passthrough variant. */
  passthrough?: false;
  /** Fallback solid background if the HDRI fails to load. */
  bgColor: number;
  /**
   * Project the HDRI onto a room-sized box instead of rendering it at infinite
   * distance, so floor, ceiling and flat walls sit at their real depth and keep
   * correct parallax as the player moves. Needed indoors, where an
   * at-infinity background makes the local floor plane read as floating and a
   * ground-projected sphere warps the walls; outdoor HDRIs need neither.
   */
  roomProjection?: {
    /** Real room dimensions (m). Tune in-headset until walls stop warping. */
    width: number;
    depth: number;
    /** Floor (y=0) to ceiling. */
    height: number;
    /** Height (m) the HDRI was photographed at; the player spawns at that spot. */
    cameraHeight: number;
  };
  floor: {
    /** Fallback color used when the floor textures are missing. */
    color: number;
    roughness: number;
    colorUrl: string;
    normalUrl: string;
    roughUrl: string;
    /** How many times to repeat the floor textures across FLOOR_SIZE_M. */
    repeat: number;
  };
};

/**
 * Passthrough ("Realtà"): the headset shows the real surroundings (immersive-ar) instead
 * of a virtual hall. No background or floor is rendered — only the HDRI's IBL is used so
 * the steel blade still reads as polished metal over the camera feed.
 */
export type PassthroughEnvironmentDef = EnvironmentBase & {
  passthrough: true;
};

export type EnvironmentDef = VirtualEnvironmentDef | PassthroughEnvironmentDef;

/** All environment assets are CC0 from Poly Haven — see CREDITS.md. */
export const ENVIRONMENTS: Record<EnvironmentId, EnvironmentDef> = {
  [ENVIRONMENT_ID.forest]: {
    id: ENVIRONMENT_ID.forest,
    bgColor: 0x6a7a5c,
    hdriUrl: '/textures/forest_slope_2k.hdr', // "Forest Slope"
    envIntensity: 0.8,
    floor: {
      color: 0x4a5238,
      roughness: 0.95,
      colorUrl: '/textures/forest_ground_color.jpg', // "Forest Ground 04"
      normalUrl: '/textures/forest_ground_normal.jpg',
      roughUrl: '/textures/forest_ground_rough.jpg',
      repeat: 6,
    },
  },
  [ENVIRONMENT_ID.salaDArmi]: {
    id: ENVIRONMENT_ID.salaDArmi,
    bgColor: 0x8a7a5e,
    hdriUrl: '/textures/old_hall_2k.hdr', // "Old Hall"
    envIntensity: 0.8,
    roomProjection: { width: 15, depth: 10, height: 7, cameraHeight: 1.6 },
    floor: {
      color: 0x6e5639,
      roughness: 0.7,
      colorUrl: '/textures/hall_floor_color.jpg', // "Wood Floor Worn"
      normalUrl: '/textures/hall_floor_normal.jpg',
      roughUrl: '/textures/hall_floor_rough.jpg',
      repeat: 4,
    },
  },
  [ENVIRONMENT_ID.passthrough]: {
    id: ENVIRONMENT_ID.passthrough,
    passthrough: true,
    // Reuse the already-shipped Old Hall HDRI for image-based lighting only; its background
    // is never shown in passthrough — the real room is. No new asset, no floor.
    hdriUrl: '/textures/old_hall_2k.hdr',
    envIntensity: 0.6,
  },
};

export const SCENE_LIGHT_HEMI_SKY = 0xb8c8a8;
export const SCENE_LIGHT_HEMI_GROUND = 0x3a3024;
/** Reduced because the HDRI already provides ambient illumination. */
export const SCENE_LIGHT_HEMI_INTENSITY = 0.35;
export const SCENE_LIGHT_DIR_COLOR = 0xfff0d8;
export const SCENE_LIGHT_DIR_INTENSITY = 0.6;

export const FLOOR_SIZE_M = 15;

export const CAMERA_FOV_DEG = 70;
export const CAMERA_NEAR_M = 0.05;
export const CAMERA_FAR_M = 50;
/** Y threshold above which the headset is considered tracked (vs camY=0 at session start). */
export const CAMERA_TRACKED_MIN_Y_M = 1;

// ─────────────────────────────────────────────────────────────
// Sword visuals (materials + secondary radii)
// ─────────────────────────────────────────────────────────────

export const SWORD_BLADE_COLOR = 0xcccccc;
export const SWORD_HILT_COLOR = 0x3a2820;
export const SWORD_BLADE_METALNESS = 0.8;
export const SWORD_BLADE_ROUGHNESS = 0.3;
export const SWORD_HILT_ROUGHNESS = 0.7;
export const SWORD_BLADE_WIDTH_BASE_M = 0.04;
export const SWORD_BLADE_WIDTH_TIP_M = 0.022;
export const SWORD_BLADE_THICKNESS_BASE_M = 0.006;
export const SWORD_BLADE_THICKNESS_TIP_M = 0.003;
export const SWORD_GRIP_RADIUS_M = 0.018;
export const SWORD_QUILLON_THICKNESS_M = 0.02;
export const SWORD_POMMEL_RADIUS_M = 0.025;

// ─────────────────────────────────────────────────────────────
// Sword GLB model (swapped in over the primitives — see sword/mesh.ts)
// ─────────────────────────────────────────────────────────────

/** "Renaissance Venetian Early Sidesword" by leeeck, CC BY 4.0 — see CREDITS.md. */
export const SWORD_MODEL_URL = '/models/sidesword.glb';
/**
 * Source-space measurements of the GLB (model units = mm; as loaded: blade
 * along +Z, pommel −Z, knuckle bow / true edge −X). Taken from the mesh
 * bounding boxes; used to scale/align the model onto the sword local frame.
 */
export const SWORD_MODEL_SRC_TIP_Z = 846.71;
/** Quillon crossbar center along the blade axis. */
export const SWORD_MODEL_SRC_GUARD_Z = -12.9;
/** Blade centerline offset from the source origin. */
export const SWORD_MODEL_SRC_AXIS_X = 19.5;
export const SWORD_MODEL_SRC_AXIS_Y = 9.8;

// ─────────────────────────────────────────────────────────────
// Pell defaults
// ─────────────────────────────────────────────────────────────

export const PELL_DEFAULT_RADIUS_M = 0.11;
export const PELL_ROUGHNESS = 0.85;

/**
 * Wood texture set (CC0 Poly Haven "Weathered Planks", 1k) — async-upgraded over
 * the fallback color (see pell/mesh.ts), shared with the targets backing disc.
 * Ships color + normal only; roughness stays constant at PELL_ROUGHNESS.
 */
export const PELL_WOOD_COLOR_URL = '/textures/pell_wood_color.jpg';
export const PELL_WOOD_NORMAL_URL = '/textures/pell_wood_normal.jpg';
/** Solid worn-timber tone shown until the wood map loads (and kept on a 404). */
export const PELL_WOOD_FALLBACK_COLOR = 0x8a6a45;
/** Vertical tiling of the wood map along the post so the grain reads at scale. */
export const PELL_WOOD_REPEAT_Y = 3;

/** Iron band near the base — a thin metallic ring that catches the HDRI. */
export const PELL_IRON_BAND_COLOR = 0x2b2622;
export const PELL_IRON_BAND_METALNESS = 0.7;
export const PELL_IRON_BAND_ROUGHNESS = 0.5;
/** Band center height as a fraction of pell height (clear of the zone lines). */
export const PELL_IRON_BAND_CENTER_FRAC = 0.04;
/** Band height (m) and how far it stands proud of the post surface (m). */
export const PELL_IRON_BAND_HEIGHT_M = 0.05;
export const PELL_IRON_BAND_OVERHANG_M = 0.004;

/**
 * Zone overlay — a transparent cylinder at radius+eps carrying a CanvasTexture.
 * Draws faint boundary lines at the tunable fractions always, and fills the
 * highlighted band with its zone tint (see PELL_ZONE_COLORS) on highlight.
 */
export const PELL_ZONE_OVERLAY_RADIUS_EPS_M = 0.002;
export const PELL_ZONE_OVERLAY_CANVAS_W = 128;
export const PELL_ZONE_OVERLAY_CANVAS_H = 1024;
export const PELL_ZONE_LINE_COLOR = '#241408'; // inchiostro su pergamena
export const PELL_ZONE_LINE_ALPHA = 0.55;
export const PELL_ZONE_LINE_WIDTH_PX = 6;
/** Alpha of the zone tint filling the highlighted band over the wood. */
export const PELL_ZONE_FILL_ALPHA = 0.5;

// ─────────────────────────────────────────────────────────────
// Hit detection (minimum tip speed for valid swing)
// ─────────────────────────────────────────────────────────────

/** Below this tip speed the swing is treated as noise (edge-alignment returns null). */
export const HIT_MIN_TIP_SPEED_MPS = 0.5;

/**
 * Validity gate for a pell/drill colpo: a contact only counts as a strike when its
 * impact tip speed AND the length of the swing arc that produced it both clear these
 * thresholds. Filters out gentle taps and tip pokes that aren't real cuts. Both are
 * live-tunable from the settings "Modalità" tab.
 */
export const PELL_STRIKE_MIN_SPEED_DEFAULT_MPS = 1.5;
export const PELL_STRIKE_MIN_SPEED_MIN_MPS = 0.5;
export const PELL_STRIKE_MIN_SPEED_MAX_MPS = 6.0;
export const PELL_STRIKE_MIN_SPEED_STEP_MPS = 0.5;

export const PELL_STRIKE_MIN_ARC_DEFAULT_M = 0.4;
export const PELL_STRIKE_MIN_ARC_MIN_M = 0.1;
export const PELL_STRIKE_MIN_ARC_MAX_M = 1.5;
export const PELL_STRIKE_MIN_ARC_STEP_M = 0.1;

/** Tip speed (m/s) above which the blade counts as "in a swing" for arc accumulation. */
export const PELL_STRIKE_ARC_MOTION_MPS = HIT_MIN_TIP_SPEED_MPS;

/**
 * 1€ filter parameters for the tip-position smoothing that drives cut *direction*
 * (filo, cut angle, lato/inclinazione). Adaptive low-pass: heavy smoothing when the
 * tip is slow (guard/jitter), minimal lag when it moves fast (the cut). See `OneEuroVec3`.
 */
export const TIP_FILTER_MIN_CUTOFF_HZ = 1.0;
export const TIP_FILTER_BETA = 1.5;
export const TIP_FILTER_DCUTOFF_HZ = 1.0;

// ─────────────────────────────────────────────────────────────
// Cut angle (white bar / cut-direction rose) — frontal-plane projection
// ─────────────────────────────────────────────────────────────

/**
 * Minimum fraction of the cut direction that must survive projection onto the
 * viewer-facing vertical plane for the angle to be meaningful. Below this the cut
 * is mostly toward/away from the viewer → angle is undefined (no white bar, excluded
 * from the rose). Placeholder value — to be tuned with real swings.
 */
export const CUT_PROJECTION_MIN_RATIO = 0.2;

/** Number of directional buckets in the cut-angle rose (45° each: down/diag/horiz/up × 2 sides). */
export const CUT_ROSE_SECTORS = 8;

// ─────────────────────────────────────────────────────────────
// Stroke classification thresholds (colpo) — inclinazione/lato/filo read the impact
// velocity (the cutting direction, same as the cut rose). Placeholders, to be tuned
// from the calibration drill's per-axis agreement data.
// ─────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────
// Prescriptive drill ("N mandritti alla testa")
// ─────────────────────────────────────────────────────────────

/** Selectable rep-count goals for the prescriptive drill. */
export const DRILL_REPS_OPTIONS = [5, 10, 20] as const;

/** Default minimum edge quality a counting rep must clear (severe — clean cut only). */
export const DRILL_MIN_EDGE_DEFAULT: EdgeQuality = EDGE_QUALITY.good;

/**
 * Elevation-angle (deg, signed; − = descending, + = ascending) bin edges for inclinazione.
 * |e| ≥ FENDENTE → near-vertical (fendente/montante by sign); ≥ DIAG → diagonal
 * (sgualembro/ridoppio); below DIAG → tondo (horizontal).
 */
export const STROKE_INCLINE_FENDENTE_DEG = 67.5;
export const STROKE_INCLINE_DIAG_DEG = 22.5;

/**
 * Minimum |perp velocity · userRight| (m/s) at impact for the lato to be decidable. Below
 * this the cut is (near-)vertical and the side is genuinely ambiguous from the impact
 * instant alone → lato null. (Lato now uses the same impact-velocity signal as the rose,
 * not the windup-contaminated arc.)
 */
export const STROKE_LATO_MIN_LATERAL_MPS = 0.5;

/**
 * Minimum perpendicular tip speed (m/s) at impact for filo to be decidable. Below this
 * neither edge clearly leads → filo null.
 */
export const STROKE_FILO_MIN_PERP_MPS = 0.5;

// ─────────────────────────────────────────────────────────────
// Guardie — RELATIONAL guard recognition (issue #16, ciclo #3 + revisione R1–R5).
// A guardia is not an absolute pose: it is WHERE YOU AIM relative to the opponent, offering
// a target as an invito. Measured on the directrix (eye-line) to the enemy — not the gaze —
// by projecting the blade ray onto the enemy: aim height (eyes/belt/off) × side (porte/code/
// distesa) + hand height. Names are d'arte → stay Italian (DESIGN.md Q15).
// ─────────────────────────────────────────────────────────────

/** Recognized guardie (right-hander convention; the lefty mirror is applied at classify time). */
export const GUARD = {
  portaDiFerroStretta: 'portaDiFerroStretta',
  portaDiFerroLarga: 'portaDiFerroLarga',
  codaLungaStretta: 'codaLungaStretta',
  codaLungaAlta: 'codaLungaAlta',
  codaLungaLarga: 'codaLungaLarga',
  codaLungaDistesa: 'codaLungaDistesa',
} as const;
export type Guard = (typeof GUARD)[keyof typeof GUARD];

/** What the guard free mode practices: every guard ('all') or one specific guardia. */
export type GuardMode = 'all' | Guard;

// ── Feature-vector axes. A guard pins a subset; the rest are wildcards. ──

/** Hand height as a fraction of user height (only `alta` is distinguished from the rest). */
export const GUARD_HAND_HEIGHT = { bassa: 'bassa', alta: 'alta' } as const;
export type GuardHandHeight = (typeof GUARD_HAND_HEIGHT)[keyof typeof GUARD_HAND_HEIGHT];

/** Where the blade ray hits the enemy vertically: at the eyes (stretta), belt (larga), or off. */
export const GUARD_AIM_HEIGHT = { occhi: 'occhi', cintola: 'cintola', fuori: 'fuori' } as const;
export type GuardAimHeight = (typeof GUARD_AIM_HEIGHT)[keyof typeof GUARD_AIM_HEIGHT];

/** Where the blade ray hits laterally: enemy's right (porte), left (code), centre, or off (distesa). */
export const GUARD_AIM_SIDE = {
  porte: 'porte',
  centro: 'centro',
  code: 'code',
  distesa: 'distesa',
} as const;
export type GuardAimSide = (typeof GUARD_AIM_SIDE)[keyof typeof GUARD_AIM_SIDE];

/**
 * Hand rotation about the grip (the roll), read from the blade's true edge: `terza` = thumb up
 * (true edge down) for the porte, `seconda` = knuckles up / palm down (true edge horizontal) for
 * the code. A held roll that is neither reads as `null` → fuori guardia.
 */
export const GUARD_HAND_ROLL = { terza: 'terza', seconda: 'seconda' } as const;
export type GuardHandRoll = (typeof GUARD_HAND_ROLL)[keyof typeof GUARD_HAND_ROLL];

/** A guard's signature: each pinned axis must match; omitted axes are "qualsiasi". */
export type GuardSignature = {
  handHeight?: GuardHandHeight;
  aimHeight?: GuardAimHeight;
  aimSide?: GuardAimSide;
  handRoll?: GuardHandRoll;
};

/**
 * Per-guard signatures (DESIGN.md revisione R1–R5). Strette aim at the eyes, larghe at the belt;
 * porte = enemy's right, code = enemy's left; distesa = off the figure; coda lunga alta aims at
 * the eyes like the stretta but with the hand raised (handHeight alta). Match rule in
 * `guardClassify`: exactly one signature → that guard, zero or ≥2 → null (fuori guardia).
 * `centro`/`fuori`/null aim reads as fuori guardia for the strette. The hand roll is pinned too:
 * the porte want the hand in terza (thumb up), the code in seconda (knuckles up).
 */
export const GUARD_SIGNATURES: Record<Guard, GuardSignature> = {
  portaDiFerroStretta: {
    handHeight: 'bassa',
    aimHeight: 'occhi',
    aimSide: 'porte',
    handRoll: 'terza',
  },
  portaDiFerroLarga: {
    handHeight: 'bassa',
    aimHeight: 'cintola',
    aimSide: 'porte',
    handRoll: 'terza',
  },
  codaLungaStretta: {
    handHeight: 'bassa',
    aimHeight: 'occhi',
    aimSide: 'code',
    handRoll: 'seconda',
  },
  codaLungaAlta: { handHeight: 'alta', aimHeight: 'occhi', aimSide: 'code', handRoll: 'seconda' },
  codaLungaLarga: {
    handHeight: 'bassa',
    aimHeight: 'cintola',
    aimSide: 'code',
    handRoll: 'seconda',
  },
  codaLungaDistesa: { aimSide: 'distesa', handRoll: 'seconda' },
};

// ── Bin thresholds (PLACEHOLDERS — to tune in-headset). Defaults feed GUARD_TUNING_DEFAULT. ──

/**
 * Hand-height fraction at/above which the hand counts as "alta" (else "bassa"). Separates the
 * coda lunga stretta/larga hand (hip height, ~0.54) from the coda lunga alta hand (last-rib
 * height, ~0.68).
 */
export const GUARD_HAND_ALTA_MIN_FRAC = 0.6;
/** Vertical tolerance (m) on the enemy around the eye / belt heights to count as occhi / cintola. */
export const GUARD_AIM_VERT_BAND_M = 0.18;
/** Lateral dead-band (m) on the enemy around centre — aim here is ambiguous (fuori guardia). */
export const GUARD_AIM_CENTRO_M = 0.05;
/** Lateral half-width (m) of the enemy figure: within = porte/code, beyond = distesa. */
export const GUARD_AIM_ON_FIG_M = 0.25;
/**
 * Hand-roll bins read the true edge (made perpendicular to the blade) against world up.
 * `terza` (thumb up): the edge points down by at least this fraction (−edge·up ≥ this).
 * `seconda` (knuckles up): the edge is near-horizontal, |edge·up| ≤ {@link GUARD_HAND_ROLL_FLAT_MAX}.
 * A roll in neither band is ambiguous → fuori guardia.
 */
export const GUARD_HAND_ROLL_DOWN_MIN = 0.5;
/** Max |edge·up| (edge near-horizontal) for the hand to count as `seconda`. */
export const GUARD_HAND_ROLL_FLAT_MAX = 0.4;
/**
 * Minimum forward component (blade · directrix) for the blade to count as aiming at the enemy.
 * Below this the point is up / sideways / backward → no aim (aimHeight fuori, aimSide null).
 */
export const GUARD_AIM_MIN_FORWARD_COS = 0.2;

// ── Enemy figure geometry (VG4) — at misura, as tall as the user. ──

/** Distance (m) in front of the user where the enemy figure stands (misura). */
export const GUARD_ENEMY_DISTANCE_M = 1.35;
/** Enemy plane width as a fraction of its (user-matched) height. */
export const GUARD_ENEMY_WIDTH_FRAC = 0.5;
/** Eye / belt heights on the enemy as a fraction of its height (the invito targets). */
export const GUARD_ENEMY_EYE_FRAC = 0.94;
export const GUARD_ENEMY_BELT_FRAC = 0.55;

// ── "In guardia" stillness gate: hand AND tip below threshold for the dwell, with hysteresis. ──
// VG1: the tip threshold is checked against the 1€-SMOOTHED tip velocity, not the raw one — the
// 95cm lever turns controller angular jitter into ~0.9 m/s of raw tip speed, which never settles.

/** Hand (grip) speed (m/s) to enter "still". */
export const GUARD_STILL_HAND_ENTER_MPS = 0.12;
/** Smoothed tip speed (m/s) to enter "still". */
export const GUARD_STILL_TIP_ENTER_MPS = 0.35;
/** Exit thresholds = enter × this (hysteresis): leaving "still" needs clearly more motion. */
export const GUARD_STILL_EXIT_FACTOR = 2.0;
/** How long (ms) the pose must stay still before it latches as "in guardia". */
export const GUARD_DWELL_MS = 250;

// ── Live-tunable guard parameters (the guard settings tab, persisted). VG2 + R-revisione. ──

/** The full set of guard-recognition parameters tunable in-headset. */
export type GuardTuning = {
  stillHandEnterMps: number;
  stillTipEnterMps: number;
  dwellMs: number;
  handAltaMinFrac: number;
  aimVertBandM: number;
  aimCentroM: number;
  aimSideOnFigM: number;
  handRollDownMin: number;
  handRollFlatMax: number;
};

export const GUARD_TUNING_DEFAULT: GuardTuning = {
  stillHandEnterMps: GUARD_STILL_HAND_ENTER_MPS,
  stillTipEnterMps: GUARD_STILL_TIP_ENTER_MPS,
  dwellMs: GUARD_DWELL_MS,
  handAltaMinFrac: GUARD_HAND_ALTA_MIN_FRAC,
  aimVertBandM: GUARD_AIM_VERT_BAND_M,
  aimCentroM: GUARD_AIM_CENTRO_M,
  aimSideOnFigM: GUARD_AIM_ON_FIG_M,
  handRollDownMin: GUARD_HAND_ROLL_DOWN_MIN,
  handRollFlatMax: GUARD_HAND_ROLL_FLAT_MAX,
};

/** Slider bounds for each tunable (settings panel + sanitization). */
export const GUARD_TUNING_RANGES: Record<
  keyof GuardTuning,
  { min: number; max: number; step: number }
> = {
  stillHandEnterMps: { min: 0.05, max: 0.5, step: 0.01 },
  stillTipEnterMps: { min: 0.1, max: 1.0, step: 0.05 },
  dwellMs: { min: 100, max: 1000, step: 50 },
  handAltaMinFrac: { min: 0.5, max: 0.9, step: 0.02 },
  aimVertBandM: { min: 0.05, max: 0.4, step: 0.01 },
  aimCentroM: { min: 0.0, max: 0.2, step: 0.01 },
  aimSideOnFigM: { min: 0.1, max: 0.6, step: 0.02 },
  handRollDownMin: { min: 0.2, max: 0.9, step: 0.05 },
  handRollFlatMax: { min: 0.1, max: 0.6, step: 0.05 },
};

// ── Spada fantasma — display-only prototype pose per guard (VG3, relational frame). ──
// Directrix frame, ~1.75m right-hander: hand = grip offset from the head (x = sword side, y = up,
// z<0 = forward toward the enemy); blade = base→tip dir aimed at the guard's target on the enemy
// at standard misura; edge = cosmetic roll. A test asserts classifyGuard(prototype) === guard.

/** User height the prototype offsets + the invariant test were authored against. */
export const GUARD_PROTOTYPE_USER_HEIGHT_M = 1.75;

export type GuardPrototype = { hand: THREE.Vector3; blade: THREE.Vector3; edge: THREE.Vector3 };

export const GUARD_PROTOTYPES: Record<Guard, GuardPrototype> = {
  portaDiFerroStretta: {
    hand: new THREE.Vector3(0.1, -0.7, -0.15),
    blade: new THREE.Vector3(-0.22, 0.7, -1.2),
    edge: new THREE.Vector3(0, -1, 0),
  },
  portaDiFerroLarga: {
    hand: new THREE.Vector3(0.1, -0.7, -0.15),
    blade: new THREE.Vector3(-0.22, 0.02, -1.2),
    edge: new THREE.Vector3(0, -1, 0),
  },
  codaLungaStretta: {
    hand: new THREE.Vector3(0.1, -0.7, -0.15),
    blade: new THREE.Vector3(0.02, 0.7, -1.2),
    edge: new THREE.Vector3(1, 0, 0),
  },
  codaLungaAlta: {
    hand: new THREE.Vector3(0.1, -0.45, -0.25),
    blade: new THREE.Vector3(0.02, 0.45, -1.1),
    edge: new THREE.Vector3(1, 0, 0),
  },
  codaLungaLarga: {
    hand: new THREE.Vector3(0.1, -0.7, -0.15),
    blade: new THREE.Vector3(0.02, 0.02, -1.2),
    edge: new THREE.Vector3(1, 0, 0),
  },
  codaLungaDistesa: {
    hand: new THREE.Vector3(0.1, -0.6, -0.1),
    blade: new THREE.Vector3(0.4, 0.6, -1.25),
    edge: new THREE.Vector3(1, 0, 0),
  },
};

/** Ghost sword material (translucent hologram); tints green when the guard is recognized. */
export const GUARD_GHOST_OPACITY = 0.35;
export const GUARD_GHOST_COLOR_HEX = 0xb8bcc0; // steel grey — neutral ghost of the held sword
export const GUARD_GHOST_MATCH_COLOR_HEX = UI_GOOD_HEX; // verdigris — recognized

/** Live aim marker on the enemy (where your blade ray lands) — VG4 / R4. */
export const GUARD_AIM_MARKER_RADIUS_M = 0.03;
export const GUARD_AIM_MARKER_COLOR_HEX = 0xffffff;

// ─────────────────────────────────────────────────────────────
// Swept hit detection (sub-stepping between frames)
// ─────────────────────────────────────────────────────────────

/**
 * Spatial precision of the inter-frame blade sweep (m). The blade is interpolated
 * through enough substeps that neither endpoint moves more than this between them,
 * so a fast cut is detected at the true surface contact instead of mid-target.
 * 0.015 ≈ contact accurate to ~1.5 cm.
 */
export const SWEEP_STEP_M = 0.015;
/** Hard cap on substeps per frame — bounds cost on a tracking glitch / teleport. */
export const SWEEP_MAX_SUBSTEPS = 32;
/**
 * Thrust tip gate: contact must land in the last fraction of the blade (near the
 * punta) to count as a thrust. 0.9 ≈ tip 10% of the blade. Live-tunable in custom mode
 * and driven by the difficulty presets (#43); higher = stricter (only the very tip counts).
 */
export const TARGETS_TIP_MIN_T_DEFAULT = 0.9;
export const TARGETS_TIP_MIN_T_MIN = 0.7;
export const TARGETS_TIP_MIN_T_MAX = 0.95;
export const TARGETS_TIP_MIN_T_STEP = 0.05;

// ─────────────────────────────────────────────────────────────
// Hit-feedback decal extras (TTL + opacity)
// ─────────────────────────────────────────────────────────────

export const DECAL_TTL_MS = 500;
export const DECAL_OPACITY = 0.9;

// ─────────────────────────────────────────────────────────────
// Audio / haptic feedback
// ─────────────────────────────────────────────────────────────

/**
 * Per-quality hit samples — one file per {@link EdgeQuality}. Files live in
 * public/audio/; a missing file degrades to silence. Replaces the old two-sample
 * clean/flat split so the three cut colours (verde/giallo/rosso) and the colpo di
 * punta each get their own voice.
 */
export const AUDIO_HIT_URLS: Record<EdgeQuality, string> = {
  good: '/audio/cut_good.wav',
  ok: '/audio/cut_ok.wav',
  flat: '/audio/cut_flat.wav',
  thrust: '/audio/thrust.wav',
};
/** Looping background-music track. Plays in every state except the active drill. */
export const AUDIO_MUSIC_URL = '/audio/music_loop.wav';
/** Tip speed (m/s) that maps to gain = 1.0; lower speeds scale linearly. */
export const AUDIO_SPEED_NORM_MPS = 8;
export const AUDIO_GAIN_MIN = 0.3;
export const AUDIO_GAIN_MAX = 1.0;

// Master volumes (0..1) for the two buses on the shared AudioManager.
export const AUDIO_SFX_VOLUME_DEFAULT = 0.8;
export const AUDIO_MUSIC_VOLUME_DEFAULT = 0.5;
export const AUDIO_VOLUME_MIN = 0;
export const AUDIO_VOLUME_MAX = 1;
/** Step for the in-VR +/- volume rows (10%). */
export const AUDIO_VOLUME_STEP = 0.1;
/** Synthesised UI click (no asset): a short tone burst on the SFX bus. */
export const AUDIO_CLICK_FREQ_HZ = 660;
export const AUDIO_CLICK_DURATION_S = 0.05;
export const AUDIO_CLICK_GAIN = 0.25;
/**
 * Synthesised "blorp" error cue (no asset): a short downward pitch sweep on the SFX bus,
 * fired when an arcade target is hit from the wrong direction. Sawtooth + falling pitch
 * reads as "rejected".
 */
export const AUDIO_ERROR_FREQ_START_HZ = 300;
export const AUDIO_ERROR_FREQ_END_HZ = 110;
export const AUDIO_ERROR_DURATION_S = 0.2;
export const AUDIO_ERROR_GAIN = 0.3;

// ─────────────────────────────────────────────────────────────
// Targets mode (mid-air thrust drill)
// ─────────────────────────────────────────────────────────────

/** Default spawn anchor distance from user (re-uses pell distance for spatial consistency). */
export const TARGETS_SPAWN_DISTANCE_M = PELL_AUTO_DISTANCE_M;
/** Live-tunable spawn distance bounds (m). */
export const TARGETS_DISTANCE_MIN_M = 0.8;
export const TARGETS_DISTANCE_MAX_M = 3.0;
export const TARGETS_DISTANCE_STEP_M = 0.1;
/** Lateral spread (rad). ±45° → uniform over [-π/4, +π/4] from forward. */
export const TARGETS_SPREAD_RAD = Math.PI / 4;
/** Outer radius of the ring (visual + hit detection). */
export const TARGETS_RING_OUTER_RADIUS_M = 0.075;
/** Inner radius of the ring (visual hole). */
export const TARGETS_RING_INNER_RADIUS_M = 0.06;
/** Small centered dot to mark "punta here". */
export const TARGETS_DOT_RADIUS_M = 0.009;
/** Target visual color (UI_SEAL_RED in three.js hex). */
export const TARGETS_COLOR_HEX = 0xb94a2a;
/** Center dot color. */
export const TARGETS_DOT_COLOR_HEX = 0xf0e6d2;
/** Color a target fades to when it misses (UI_INK_MUTED-ish gray). */
export const TARGETS_MISS_COLOR_HEX = 0x6b6b6b;

/**
 * Wooden backing disc behind the ring — turns each target into a roundel tied to
 * the post. Reuses the pell wood color map (no new asset); the per-instance
 * material fades on hit/miss but never grays (see targetsRuntime).
 */
export const TARGETS_DISC_RADIUS_FACTOR = 1.25; // × ring outer radius
export const TARGETS_DISC_Z_OFFSET_M = -0.001; // just behind the ring
export const TARGETS_DISC_SEGMENTS = 32;
/** Solid tone shown until the shared wood map loads (matches the pell fallback). */
export const TARGETS_DISC_FALLBACK_COLOR_HEX = PELL_WOOD_FALLBACK_COLOR;

/** Live-tunable time (ms) a target stays alive before it auto-misses ("scomparsa"). */
export const TARGETS_TTL_DEFAULT_MS = 2500;
export const TARGETS_TTL_MIN_MS = 500;
export const TARGETS_TTL_MAX_MS = 8000;
export const TARGETS_TTL_STEP_MS = 500;

/** Grace delay before the first spawn at session start (ms). */
export const TARGETS_FIRST_SPAWN_DELAY_MS = 1000;

/** Live-tunable delay (ms) between target resolution (hit/miss) and the next spawn ("comparsa"). */
export const TARGETS_SPAWN_DELAY_DEFAULT_MS = 700;
export const TARGETS_SPAWN_DELAY_MIN_MS = 0;
export const TARGETS_SPAWN_DELAY_MAX_MS = 3000;
export const TARGETS_SPAWN_DELAY_STEP_MS = 100;

/** Hit animation duration (ms) — fade out + scale up. */
export const TARGETS_HIT_ANIM_MS = 150;
/** Hit animation peak scale (1.0 = unchanged). */
export const TARGETS_HIT_ANIM_SCALE = 1.4;
/** Miss animation duration (ms) — gray fade. */
export const TARGETS_MISS_ANIM_MS = 300;

/** Spawn weight per zone (must sum to 1; head/shoulder/chest dominate). */
export const TARGETS_ZONE_WEIGHTS: Record<Zone, number> = {
  head: 0.25,
  shoulder: 0.25,
  chest: 0.3,
  flank: 0.15,
  leg: 0.05,
};

/** Live-tunable size multiplier (debug/calibration). 1.0 = stock geometry. */
export const TARGETS_SIZE_FACTOR_DEFAULT = 1.0;
export const TARGETS_SIZE_FACTOR_MIN = 1.0;
export const TARGETS_SIZE_FACTOR_MAX = 3.0;
export const TARGETS_SIZE_FACTOR_STEP = 0.25;

/** Live-tunable minimum tip speed for thrust validity (m/s). */
export const TARGETS_MIN_SPEED_DEFAULT_MPS = 0.5;
export const TARGETS_MIN_SPEED_MIN_MPS = 0.0;
export const TARGETS_MIN_SPEED_MAX_MPS = 2.0;
export const TARGETS_MIN_SPEED_STEP_MPS = 0.1;

/** Tip speed (m/s) that maps to haptic intensity = 1.0. */
export const HAPTIC_SPEED_NORM_MPS = 10;
export const HAPTIC_INTENSITY_MIN = 0.3;
export const HAPTIC_INTENSITY_MAX = 1.0;
export const HAPTIC_PULSE_MS = 80;

// ─────────────────────────────────────────────────────────────
// Arcade mode ("Sfida" — issue #34): 60s directional-cut score attack.
// Reuses the targets phase machine + sphere sweep, gated on an edge-leading cut
// whose direction must match the disc's required rose sector.
// All tuning values are PLACEHOLDERS — validate the feel in-headset.
// ─────────────────────────────────────────────────────────────

/** Canonical fixed run length (ms). Single length so scores are comparable (epic #33). */
export const ARCADE_DURATION_MS = 60_000;

/** Live-tunable target lifetime before auto-miss (ms). Faster cadence than the targets drill. */
export const ARCADE_TTL_DEFAULT_MS = 2000;
export const ARCADE_TTL_MIN_MS = 800;
export const ARCADE_TTL_MAX_MS = 5000;
export const ARCADE_TTL_STEP_MS = 200;

/** Live-tunable delay between a target resolving and the next spawn (ms). */
export const ARCADE_SPAWN_DELAY_DEFAULT_MS = 350;
export const ARCADE_SPAWN_DELAY_MIN_MS = 0;
export const ARCADE_SPAWN_DELAY_MAX_MS = 1500;
export const ARCADE_SPAWN_DELAY_STEP_MS = 50;

/**
 * Live-tunable angular tolerance (deg) between the read cut direction and the disc's
 * required direction for the cut to count as "correct". Too tight = frustrating, too
 * loose = trivial. 30° ≈ a touch wider than one rose sector half-width (22.5°).
 */
export const ARCADE_CUT_DIR_TOLERANCE_DEFAULT_DEG = 30;
export const ARCADE_CUT_DIR_TOLERANCE_MIN_DEG = 15;
export const ARCADE_CUT_DIR_TOLERANCE_MAX_DEG = 60;
export const ARCADE_CUT_DIR_TOLERANCE_STEP_DEG = 5;

/** Live-tunable disc size multiplier (mesh + hit sphere). 1.0 = stock geometry. */
export const ARCADE_SIZE_FACTOR_DEFAULT = 1.0;
export const ARCADE_SIZE_FACTOR_MIN = 1.0;
export const ARCADE_SIZE_FACTOR_MAX = 3.0;
export const ARCADE_SIZE_FACTOR_STEP = 0.25;

/** Grace delay before the first spawn at run start (ms). */
export const ARCADE_FIRST_SPAWN_DELAY_MS = TARGETS_FIRST_SPAWN_DELAY_MS;

/**
 * Minimum tip speed (m/s) for a slice to count (below this it is noise, not a cut). Live-tunable
 * in custom mode and driven by the difficulty presets (#43); higher = must swing harder.
 */
export const ARCADE_MIN_SPEED_DEFAULT_MPS = 1.0;
export const ARCADE_MIN_SPEED_MIN_MPS = 0.5;
export const ARCADE_MIN_SPEED_MAX_MPS = 3.0;
export const ARCADE_MIN_SPEED_STEP_MPS = 0.1;

// ── Scoring (combo-at-risk) ──

/** Base points for a correct-direction hit before multipliers. */
export const ARCADE_BASE_POINTS = 100;
/** Consecutive clean hits needed to add +1 to the combo multiplier. */
export const ARCADE_COMBO_STEP = 3;
/** Cap on the combo multiplier so a long streak can't run away. */
export const ARCADE_COMBO_MAX_MULT = 8;

/**
 * Edge-quality sub-multiplier on a correct-direction hit. `flat` is a "sloppy" cut: it
 * still scores (reduced) and keeps the combo, but does not grow it. `thrust` is unused
 * here (a thrust never resolves an arcade target) but kept for the exhaustive record.
 */
export const ARCADE_EDGE_MULT: Record<EdgeQuality, number> = {
  good: 1.0,
  ok: 0.75,
  flat: 0.4,
  thrust: 0,
};

// ── Thrust (punte) scoring — #44 ──
// The thrust analogue of edge quality is *centering*: a hit near the disc center is
// "clean" (full points, grows the combo); a hit out toward the rim is "sloppy" (reduced,
// keeps the combo). Speed stays a gate (TARGETS_MIN_SPEED_*), never a multiplier.

/** A thrust hit within this fraction of the ring radius counts as a centered ("clean") hit. */
export const ARCADE_THRUST_CLEAN_FRAC = 0.5;

/** Centering sub-multiplier on a thrust hit — mirrors {@link ARCADE_EDGE_MULT} for cuts. */
export const ARCADE_THRUST_MULT = {
  clean: 1.0,
  sloppy: 0.5,
} as const;

// ── Difficulty presets (#43) ──
// Read-only bundles over the existing arcade tunables, one per technique (their knobs differ:
// cut accepts by direction tolerance, thrust by tip-gate). Selecting a preset feeds the runtime
// from here; "custom" reads the settings sliders instead. Values are PLACEHOLDERS — tune in
// headset. Monotonic toward harder: less time, faster cadence, smaller disc, faster swing, and
// a tighter acceptance (cut: narrower tolerance; thrust: only the very tip). `sizeFactor` floors
// at 1.0 (stock), so it can only enlarge the disc for the easy end.

export type ArcadeCutTuning = {
  ttlMs: number;
  spawnDelayMs: number;
  sizeFactor: number;
  minSpeedMps: number;
  cutToleranceDeg: number;
};

export type ArcadeThrustTuning = {
  ttlMs: number;
  spawnDelayMs: number;
  sizeFactor: number;
  minSpeedMps: number;
  tipMinT: number;
};

export const ARCADE_CUT_DIFFICULTY: Record<DifficultyPreset, ArcadeCutTuning> = {
  easy: { ttlMs: 3000, spawnDelayMs: 600, sizeFactor: 1.5, minSpeedMps: 0.8, cutToleranceDeg: 45 },
  medium: {
    ttlMs: 2200,
    spawnDelayMs: 400,
    sizeFactor: 1.2,
    minSpeedMps: 1.0,
    cutToleranceDeg: 33,
  },
  hard: { ttlMs: 1500, spawnDelayMs: 250, sizeFactor: 1.0, minSpeedMps: 1.4, cutToleranceDeg: 24 },
  xhard: { ttlMs: 1000, spawnDelayMs: 150, sizeFactor: 1.0, minSpeedMps: 1.8, cutToleranceDeg: 18 },
};

export const ARCADE_THRUST_DIFFICULTY: Record<DifficultyPreset, ArcadeThrustTuning> = {
  easy: { ttlMs: 3500, spawnDelayMs: 1000, sizeFactor: 1.6, minSpeedMps: 0.2, tipMinT: 0.8 },
  medium: { ttlMs: 2500, spawnDelayMs: 700, sizeFactor: 1.25, minSpeedMps: 0.5, tipMinT: 0.88 },
  hard: { ttlMs: 1700, spawnDelayMs: 400, sizeFactor: 1.0, minSpeedMps: 0.9, tipMinT: 0.92 },
  xhard: { ttlMs: 1100, spawnDelayMs: 200, sizeFactor: 1.0, minSpeedMps: 1.3, tipMinT: 0.95 },
};

// ── Shared leaderboard (issue #35) ──

/**
 * Scoring version stamped on every leaderboard row. Bump when the arcade scoring core
 * changes so future code can segment incomparable scores instead of mixing them.
 */
export const ARCADE_RUN_VERSION = 1;

/** Scoring version for the separate thrust (punte) board — independent from the cut one (#44). */
export const ARCADE_THRUST_RUN_VERSION = 1;

/**
 * Hard upper bound on a leaderboard score, enforced both client-side and in
 * `firestore.rules` (the rules can't import this — keep the literal in sync there).
 * Derivation: max points per hit = ARCADE_BASE_POINTS(100) × ARCADE_COMBO_MAX_MULT(8) ×
 * best edge mult(1.0) = 800; a generous ~4 hits/s over the 60s run ⇒ 800 × 4 × 60 = 192 000,
 * rounded up. Anything above this is implausible and rejected as tampering.
 */
export const LEADERBOARD_SCORE_CAP = 200_000;

/** Max nickname length (chars), enforced client-side and in `firestore.rules`. */
export const LEADERBOARD_NICKNAME_MAX_LEN = 24;

/** How many top rows the post-run rank panel fetches. */
export const LEADERBOARD_TOP_N = 5;

/** How many top rows the 2D landing board fetches (scrollable, paginated/limited). */
export const LEADERBOARD_LANDING_LIMIT = 50;

// ── Directional arrow on the disc (billboarded, in the disc plane) ──

/** Arrow length / head size as a fraction of the ring outer radius. */
export const ARCADE_ARROW_LENGTH_FACTOR = 1.6;
export const ARCADE_ARROW_HEAD_FACTOR = 0.7;
export const ARCADE_ARROW_WIDTH_M = 0.012;
/** Just in front of the ring/dot to avoid z-fighting. */
export const ARCADE_ARROW_Z_OFFSET_M = 0.002;
export const ARCADE_ARROW_COLOR_HEX = 0xf0e6d2; // UI ivory — reads on the seal-red ring

// ── Wrong-direction marker: a red X flashed over the roundel on a wrong-side hit ──
// Added to the dying group so it fades within the existing miss animation window
// (TARGETS_MISS_ANIM_MS) — never extends the time before the next target spawns.

/** Bright red — a clear "sbagliato" against the grayed-out roundel. */
export const ARCADE_WRONG_COLOR_HEX = 0xef4444;
/** Bar length as a fraction of the ring outer radius (≈ spans the backing disc). */
export const ARCADE_WRONG_LENGTH_FACTOR = 2.4;
/** Bar thickness (m) — a touch bolder than the arrow so the X reads at a glance. */
export const ARCADE_WRONG_WIDTH_M = 0.016;
/** Just in front of the arrow to avoid z-fighting. */
export const ARCADE_WRONG_Z_OFFSET_M = 0.003;

// ── Off-screen target indicator — a mini target roundel pinned to the FOV edge ──
// When a target spawns outside the field of view (e.g. the user is looking right and
// the next target appears on the left), a small copy of the target — terracotta ring +
// cream center dot on a cream halo — is shown at the view edge in the target's direction
// so the user knows where to turn. Used by both the arcade and the thrust drill. The
// halo gives high peripheral contrast against forest/wood backdrops, and reusing the
// target's own colors makes the hint read as "your target is over there".

/** Cream halo radius (m) behind the ring — the high-contrast backing for periphery. */
export const OFFSCREEN_HALO_RADIUS_M = 0.034;
/** Cream halo color — the target's center cream, so the glyph reads as kin to the target. */
export const OFFSCREEN_HALO_COLOR_HEX = TARGETS_DOT_COLOR_HEX;
/** Halo opacity — semi-transparent so it reads as a glow, not an opaque plate. */
export const OFFSCREEN_HALO_OPACITY = 0.55;
/** Outer / inner radius (m) of the terracotta ring — a scaled-down target roundel. */
export const OFFSCREEN_RING_OUTER_RADIUS_M = 0.026;
export const OFFSCREEN_RING_INNER_RADIUS_M = 0.018;
/** Ring color — the target's own terracotta. */
export const OFFSCREEN_RING_COLOR_HEX = TARGETS_COLOR_HEX;
/** Center dot radius (m) + color — the target's cream center. */
export const OFFSCREEN_DOT_RADIUS_M = 0.008;
export const OFFSCREEN_DOT_COLOR_HEX = TARGETS_DOT_COLOR_HEX;
/** Forward distance (m) the glyph is pinned in front of the head. */
export const OFFSCREEN_DOT_DISTANCE_M = 0.6;
/**
 * Cone half-angle (deg) from view-center at which the glyph sits, toward the target.
 * A fixed, comfortable angle — NOT the literal frustum edge — so it stays inside
 * what *each* eye renders on the headset (the XR union frustum the FOV is read from is
 * wider than a single eye, so placing at its edge would clip the glyph out of view).
 */
export const OFFSCREEN_EDGE_ANGLE_DEG = 28;
/** Show the glyph once the target passes this fraction of the FOV half-extent. */
export const OFFSCREEN_SHOW_MARGIN = 0.96;

// ─────────────────────────────────────────────────────────────
// Wrist-mounted HUD (canvas + placement on off-hand controller)
// ─────────────────────────────────────────────────────────────

export const HUD_CANVAS_W_PX = 512;
export const HUD_CANVAS_H_PX = 256;
export const HUD_PLANE_WIDTH_M = 0.16;
export const HUD_PLANE_HEIGHT_M = 0.08;
export const HUD_OFFSET_X_M = 0;
export const HUD_OFFSET_Y_M = 0.04;
export const HUD_OFFSET_Z_M = 0.06;
export const HUD_PITCH_RAD = -Math.PI / 3;

// ─────────────────────────────────────────────────────────────
// Menu pointer (#39): a quill whose nib rests on the panel where the
// controller aims (it is the cursor), replacing the old red laser beam.
// ─────────────────────────────────────────────────────────────

/** Quill length (m); the texture aspect sets the width. */
export const QUILL_LENGTH_M = 0.18;
/** Aspect (width / height) of ui_quill.png (242 × 477). */
export const QUILL_ASPECT = 242 / 477;
/** Quill opacity (slightly soft so it does not dominate the panel). */
export const QUILL_OPACITY = 0.95;

/** Lift (m) of the quill off the panel toward the camera, to avoid z-fighting. */
export const QUILL_LIFT_M = 0.002;

// ─────────────────────────────────────────────────────────────
// User height (defaults + auto-detect from camera Y)
// ─────────────────────────────────────────────────────────────

export const USER_HEIGHT_DEFAULT_CM = 175;
export const USER_HEIGHT_LOAD_MIN_CM = 100;
export const USER_HEIGHT_LOAD_MAX_CM = 220;
/** Added to camera Y to approximate user height (eye → top-of-head). */
export const HEIGHT_DETECT_CAMERA_OFFSET_M = 0.1;
/** Auto-override stored height with detected one if they differ by more than this. */
export const HEIGHT_DETECT_OVERRIDE_THRESHOLD_CM = 5;
